import {
  OrderResponseDTOOrderStatusEnum,
  OrderWarehouseInfoDTOPackageStatusEnum,
  type OrderResponseDTO,
  type PurchaserInfoResponseDTO,
} from '@sellsen/generated-sources';
import { message } from 'antd';
import { formatOrderStatusLabel } from './order-status';

export const SELLER_CENTRAL_ORDERS_URL =
  'https://sellercentral.amazon.co.uk/orders-v3';

/**
 * Eklentiye devir adresi. Sabit `https://sellsen.com` yerine bulunulan origin kullanılır:
 * prod'da sonuç aynıdır, local'de ise akış prod siteye atlamaz (orada oturum olmadığı için
 * yönlendirme parametreleri düşürüyor ve eklenti hiç tetiklenmiyordu).
 */
export const autoOrderBaseUrl = () =>
  `${typeof window !== 'undefined' ? window.location.origin : 'https://sellsen.com'}/autoorder`;

export const EXTENSION_PURCHASER_WARNING =
  'Sipariş bilgileri eksik. Lütfen eklenti ile siparişleri aktarınız.';

/** Eksik alıcı verisinde SC sipariş listesine yönlendirme linki metni */
export const EXTENSION_IMPORT_ORDER_LINK = 'Siparişi eklenti ile aktar';

const hasText = (value?: string) => Boolean(value?.trim());

/** Giriş yapan satıcının ShippingLot child id'si (ör. SL761); eklenti adres satır 2'ye yazar. */
let autoOrderCustomerSlId = '';

/** Web oturumunun username'i; eklenti JWT kullanıcısıyla karşılaştırılır. */
let autoOrderOwnerUsername = '';

export const setAutoOrderCustomerSlId = (slId?: string) => {
  autoOrderCustomerSlId = slId?.trim() || '';
};

export const setAutoOrderOwnerUsername = (username?: string) => {
  autoOrderOwnerUsername = username?.trim() || '';
};

export const isUnshipped = (
  order: Pick<OrderResponseDTO, 'orderStatus'> | undefined
) => order?.orderStatus === OrderResponseDTOOrderStatusEnum.Unshipped;

/**
 * Amazon sipariş durumu satın almayı engellemez — sipariş geciktiğinde kargo onayı Amazon
 * tarafında verilmiş olabiliyor, paket ise hâlâ satın alınmayı bekliyor olur. Backend de
 * kısıtlamıyor; durum UNSHIPPED değilse kullanıcıyı yalnız bilgilendiriyoruz.
 */
export const purchaseStatusNotice = (
  order: Pick<OrderResponseDTO, 'orderStatus'> | undefined
) =>
  order?.orderStatus && !isUnshipped(order)
    ? `Bu sipariş Amazon'da "${formatOrderStatusLabel(
        order.orderStatus
      )}" görünüyor; satın alma yine de yapılabilir.`
    : null;

/** Satın alma yalnız paket henüz satın alınmamışken anlamlı. */
export const isPackageNotPurchased = (
  order: Pick<OrderResponseDTO, 'warehouseInfo'> | undefined
) =>
  order?.warehouseInfo?.packageStatus ===
  OrderWarehouseInfoDTOPackageStatusEnum.NotPurchased;

/**
 * ShippingLot'ta bu paket için açılmış bir sipariş var mı? Rate-first akışında depo siparişi
 * Amazon satın almasından ÖNCE açıldığı için bu, "satın alındı" demek DEĞİLDİR.
 */
export const hasWarehouseOrder = (
  order: Pick<OrderResponseDTO, 'warehouseInfo'> | undefined
) => Boolean(order?.warehouseInfo?.shippinglotOrderId?.trim());

/**
 * Depo siparişi ShippingLot panelinden iptal edilmiş mi? Panel iptali (webhook) paketi geri
 * almaz ve `shippinglotOrderId`'yi silmez, o yüzden "sipariş var mı" ile "sipariş yaşıyor mu" ayrı
 * sorulardır. Satın almayı geri almak da siparişi iptal ETMEZ (RT ve adres korunur); yalnız açık
 * "depo siparişini iptal et" ve taşıyıcı değişikliği iptal eder, o paketler doğuş durumuna döner.
 */
export const isWarehouseOrderCancelled = (
  order: Pick<OrderResponseDTO, 'warehouseInfo'> | undefined
) => order?.warehouseInfo?.shippinglotStatus?.trim().toLowerCase() === 'cancelled';

/**
 * ShippingLot 30 gün ürün gelmeyen siparişi `stale`'e çeker ve 7 gün içinde kendisi iptal eder;
 * sipariş fiilen ölüdür. Backend `WarehousePackage.isWarehouseOrderLive` ile aynı kural.
 */
export const isWarehouseOrderStale = (
  order: Pick<OrderResponseDTO, 'warehouseInfo'> | undefined
) => order?.warehouseInfo?.shippinglotStatus?.trim().toLowerCase() === 'stale';

/** Paketi hâlâ bekleyen, canlı bir depo siparişi var mı? */
export const hasActiveWarehouseOrder = (
  order: Pick<OrderResponseDTO, 'warehouseInfo'> | undefined
) =>
  hasWarehouseOrder(order) &&
  !isWarehouseOrderCancelled(order) &&
  !isWarehouseOrderStale(order);

const isSubmittedToWarehouse = (
  order: Pick<OrderResponseDTO, 'warehouseInfo'> | undefined
) =>
  order?.warehouseInfo?.packageStatus ===
  OrderWarehouseInfoDTOPackageStatusEnum.SubmittedToWh;

/**
 * Depo siparişi açık, Amazon satın alması henüz kaydedilmedi (ilk gönderimden sonra ya da satın
 * alma geri alındıktan sonra). Satın Al bu pakette de taşıyıcı modalını açar: mevcut taşıyıcı
 * seçili gelir, mevcutla devam edilirse sipariş ve RT aynen kalır ve Amazon aynı adresle açılır;
 * farklı taşıyıcı seçilirse eski sipariş iptal edilip yenisi açılır (yeni RT).
 */
export const isPackageAwaitingPurchase = (
  order: Pick<OrderResponseDTO, 'warehouseInfo'> | undefined
) => isSubmittedToWarehouse(order) && hasActiveWarehouseOrder(order);

/**
 * Depo siparişi açılmıştı ama ShippingLot tarafında öldü (panelden iptal / stale) ve paket hâlâ
 * alım bekliyor. Bu adresle satın alınamaz; paket önce doğuş durumuna döndürülüp yeni bir depo
 * siparişiyle gönderilmelidir.
 */
export const isPackageAwaitingResubmission = (
  order: Pick<OrderResponseDTO, 'warehouseInfo'> | undefined
) =>
  isSubmittedToWarehouse(order) &&
  hasWarehouseOrder(order) &&
  !hasActiveWarehouseOrder(order) &&
  // Satıcının "depoya gelmeyecek" işareti: geri alma Depo gönderimi menüsünden yapılır.
  !order?.warehouseInfo?.warehouseCancelledAt;

/** "Satın alınmadı" etiketinin, depo siparişi açık paketteki açıklaması. */
export const awaitingPurchaseNotice = (
  order: Pick<OrderResponseDTO, 'warehouseInfo'> | undefined
) =>
  isPackageAwaitingPurchase(order)
    ? `Depo siparişi açık (${order?.warehouseInfo?.shippinglotOrderId}), alım bekleniyor. Aynı adresle satın alın.`
    : isPackageAwaitingResubmission(order)
      ? `Depo siparişi ${order?.warehouseInfo?.shippinglotOrderId} ShippingLot'ta iptal edilmiş; paket yeni bir depo siparişiyle yeniden gönderilmeli.`
      : null;

export const hasExtensionPurchaserInfo = (
  order: Pick<OrderResponseDTO, 'purchaserInfo'> | undefined
) => {
  const info = order?.purchaserInfo;
  return (
    hasText(info?.buyerName) &&
    hasText(info?.addressLine) &&
    hasText(info?.phone)
  );
};

export const isExtensionPurchaserFieldMissing = (
  info: PurchaserInfoResponseDTO | undefined,
  field: 'buyerName' | 'addressLine' | 'phone'
) => !hasText(info?.[field]);

/**
 * Eklentinin Amazon'da kullanacağı satın alma bağlantısı.
 *
 * Adres formatı:
 *   Shippinglot {sl_order_id}
 *   1 Madison Street
 *   F10 {sl_id}
 *   East Rutherford, NJ 07073
 *
 * `sl_order_id` yalnız <b>submit edilmiş</b> depo siparişinin kimliğidir — taslak kimliği
 * `replace_draft` ile başka siparişe devredilebildiği için kullanılmaz.
 */
export const buildAutoOrderUrl = (
  order: Pick<OrderResponseDTO, 'warehouseInfo' | 'orderItems'> | undefined
): string | null => {
  const warehousePackageId = order?.warehouseInfo?.warehousePackageId;
  if (warehousePackageId == null) {
    return null;
  }

  const items = (order?.orderItems ?? [])
    .filter((item) => hasText(item.asin))
    .map((item) => `${item.asin}:${item.quantityOrdered ?? 1}`);

  if (items.length === 0) {
    return null;
  }

  const params = new URLSearchParams({
    external_order_id: `SS-${warehousePackageId}`,
    items: items.join(','),
  });

  const slOrderId = order?.warehouseInfo?.shippinglotOrderId;
  if (hasText(slOrderId)) {
    params.set('sl_order_id', slOrderId as string);
  }

  const slId =
    autoOrderCustomerSlId ||
    (order?.warehouseInfo as {shippinglotChildId?: string} | undefined)
      ?.shippinglotChildId;
  if (hasText(slId)) {
    params.set('sl_id', slId as string);
  }

  if (hasText(autoOrderOwnerUsername)) {
    params.set('owner', autoOrderOwnerUsername);
  }

  return `${autoOrderBaseUrl()}?${params.toString()}`;
};

export const canBuildAutoOrderUrl = (
  order: Pick<OrderResponseDTO, 'warehouseInfo' | 'orderItems'> | undefined
) => buildAutoOrderUrl(order) != null;

/**
 * Eklentiyi açar. Depo siparişi açılmış olmalıdır — akışın başlangıcı taşıyıcı seçimi modalıdır,
 * bu fonksiyon yalnız modaldan (submit sonrası ya da "mevcut taşıyıcıyla devam") çağrılır.
 */
export const openAutoOrder = (
  order: Pick<OrderResponseDTO, 'warehouseInfo' | 'orderItems'> | undefined
) => {
  const url = buildAutoOrderUrl(order);
  if (!url) {
    message.warning('Satın alma için gerekli sipariş bilgileri eksik.');
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};
